import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './SurveyPage.css';

/* ---------------- small helper to log to backend ---------------- */
async function logEvent(event_type, details = {}) {
  try {
    await fetch('/api/log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ event_type, details }),
    });
  } catch (e) {
    // non-fatal
  }
}

/* ---------------- reusable Likert question ---------------- */
const LikertQuestion = ({ question, name, scale, formData, onChange }) => (
  <div className="survey-question" role="group" aria-labelledby={`${name}-label`}>
    <label id={`${name}-label`} className="survey-question-text">
      {question} <span aria-hidden="true">*</span>
    </label>

    <div className="likert-wrap" style={{ '--points': scale.length }}>
      <div className="likert-labels">
        <span className="left">Strongly Disagree</span>
        <span className="right">Strongly Agree</span>
      </div>

      <div className="likert-scale">
        {scale.map((point, idx) => (
          <label key={idx} className="likert-point">
            <input
              type="radio"
              name={name}
              value={String(point)}
              checked={formData[name] === String(point)}
              onChange={(e) => onChange(name, e.target.value)}
              required
            />
            <span>{point}</span>
          </label>
        ))}
      </div>
    </div>
  </div>
);

function PostSurveyPage() {
  const navigate = useNavigate();

  /* ---------------- constants / sections ---------------- */
  const scale7 = ['1', '2', '3', '4', '5', '6', '7'];
  const sections = useMemo(() => ([
    {
      key: 'section1',
      title: 'Learning Outcomes & Experience',
      scale: scale7,
      questions: [
        { name: 'learned_approach', text: 'I learned a better process or workflow (natural language description, then code) to solve this type of algorithmic problem.' },
        { name: 'improved_understanding', text: 'This activity improved my understanding of the core concept(s) in this problem.' },
        { name: 'code_confidence', text: 'I am confident that my submitted code correctly solves this problem.' },
        { name: 'more_time_confidence', text: 'Given more time, I would be able to write correct code to solve this problem.' },
        { name: 'solve_similar_confidence', text: 'After this session, I feel confident solving similar problems on my own.' },
        { name: 'workflow_helped_breakdown', text: 'The workflow (natural language description, then code) helped me break the problem into clear, manageable steps.' },
        // { name: 'identified_edge_cases', text: 'I identified edge cases I would likely have missed otherwise.' },
        { name: 'can_explain_approach', text: 'I can clearly explain my approach in plain language.' },
        { name: 'have_skills', text: 'I have the skills needed to solve problems like this.' },
        { name: 'felt_accomplishment', text: 'I felt a sense of accomplishment while solving the task.' },
        { name: 'intend_to_use_workflow', text: 'I intend to use the this workflow (natural language description, then code) when solving similar algorithmic problems in the future.' },
      ],
    },
    {
      key: 'section2',
      title: 'Usability (UX-Lite)',
      scale: scale7,
      questions: [
        { name: 'easy_to_use', text: 'The system is easy to use.' },
        { name: 'met_requirements', text: 'The system’s capabilities meet my requirements.' },
      ],
    },
    {
      key: 'section3',
      title: 'Cognitive Load',
      scale: scale7,
      questions: [
        { name: 'task_was_complex', text: 'The leetcode question was very complex.' },
        { name: 'interface_made_harder', text: 'The interface or instructions made the task harder than necessary.' },
        { name: 'encouraged_effort', text: 'This workflow (natural language description, then code) encouraged me to invest mental effort in understanding the problem.' },
      ],
    }
  ]), []);

  const totalSteps = sections.length;

  /* ---------------- hydrate step + formData ---------------- */
  const [step, setStep] = useState(() => {
    const raw = localStorage.getItem('rt_post_survey_step');
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 && n < totalSteps ? n : 0;
  });

  const [formData, setFormData] = useState(() => {
    try { return JSON.parse(localStorage.getItem('rt_post_survey_draft') || '{}'); }
    catch { return {}; }
  });

  /* ---------------- timers (survive refresh) ---------------- */
  const surveyStartRef = useRef(Date.now());
  const sectionStartRef = useRef(Date.now());

  useEffect(() => {
    try {
      localStorage.setItem('rt_flow_stage', 'post_survey');
      localStorage.setItem('rt_last_route', '/post-survey');
    } catch {}
  }, []);
  

  // Restore timers from storage (if present) or initialize them.
  useEffect(() => {
    try {
      const savedSurveyStart = Number(localStorage.getItem('rt_post_survey_started_at'));
      const savedSectionStart = Number(localStorage.getItem('rt_post_survey_section_started_at'));
      if (Number.isFinite(savedSurveyStart)) surveyStartRef.current = savedSurveyStart;
      else {
        surveyStartRef.current = Date.now();
        localStorage.setItem('rt_post_survey_started_at', String(surveyStartRef.current));
      }
      if (Number.isFinite(savedSectionStart)) sectionStartRef.current = savedSectionStart;
      else {
        sectionStartRef.current = Date.now();
        localStorage.setItem('rt_post_survey_section_started_at', String(sectionStartRef.current));
      }
    } catch {}
  }, []);

  // When step changes, reset section start + persist
  useEffect(() => {
    try {
      sectionStartRef.current = Date.now();
      localStorage.setItem('rt_post_survey_section_started_at', String(sectionStartRef.current));
    } catch {}
  }, [step]);

  /* ---------------- autosave state on change ---------------- */
  useEffect(() => {
    try { localStorage.setItem('rt_post_survey_draft', JSON.stringify(formData)); } catch {}
  }, [formData]);

  useEffect(() => {
    try { localStorage.setItem('rt_post_survey_step', String(step)); } catch {}
  }, [step, totalSteps]);

  /* ---------------- flush on unload / pagehide ---------------- */
  useEffect(() => {
    const flush = () => {
      try {
        localStorage.setItem('rt_post_survey_draft', JSON.stringify(formData));
        localStorage.setItem('rt_post_survey_step', String(step));
        localStorage.setItem('rt_post_survey_started_at', String(surveyStartRef.current));
        localStorage.setItem('rt_post_survey_section_started_at', String(sectionStartRef.current));
      } catch {}
    };
    window.addEventListener('beforeunload', flush);
    window.addEventListener('pagehide', flush);
    return () => {
      window.removeEventListener('beforeunload', flush);
      window.removeEventListener('pagehide', flush);
    };
  }, [formData, step]);

  /* ---------------- analytics: open + first section view ---------------- */
  useEffect(() => {
    logEvent('post_survey_open', { ts: surveyStartRef.current });
    logEvent('post_survey_section_view', { section: sections[step]?.key, index: step });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [error, setError] = useState('');
  const current = sections[Math.min(step, totalSteps - 1)];

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    logEvent('post_survey_answer', { name, value, section: current.key });
    if (error) setError('');
  };

  const validateSection = () => {
    const missing = current.questions.filter(q => !formData[q.name]);
    if (missing.length > 0) {
      setError('Please answer all required questions in this section.');
      return false;
    }
    return true;
  };

  const goToStep = (nextIndex, action) => {
    if (nextIndex < 0 || nextIndex >= totalSteps) return;

    const now = Date.now();
    const dwellMs = now - sectionStartRef.current;

    logEvent('post_survey_section_dwell', {
      section: current.key,
      index: step,
      dwell_ms: dwellMs,
      action,
    });

    sectionStartRef.current = now;
    try { localStorage.setItem('rt_post_survey_section_started_at', String(now)); } catch {}

    setStep(nextIndex);
    logEvent('post_survey_section_view', { section: sections[nextIndex].key, index: nextIndex });

    window.scrollTo({ top: 0, behavior: 'auto' });
  };

  const handleNext = () => {
    if (!validateSection()) return;
    if (step < totalSteps - 1) goToStep(step + 1, 'next');
  };

  const handleBack = () => {
    setError('');
    if (step > 0) goToStep(step - 1, 'back');
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    // if not last section, guard-advance
    if (step < totalSteps - 1) {
      if (!validateSection()) return;
      goToStep(step + 1, 'submit_guard_advance');
      return;
    }
    if (!validateSection()) return;

    const totalDurationMs = Date.now() - surveyStartRef.current;
    logEvent('post_survey_submit_attempt', {
      duration_ms: totalDurationMs,
      answers_count: Object.keys(formData).length,
    });

    try {
      const resp = await fetch('/api/submit_post_survey', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      if (resp.ok) {
        logEvent('post_survey_submit_success', {
          duration_ms: totalDurationMs,
          answers: formData,
        });
        // snapshot the successful submission
        try {
          localStorage.setItem('rt_post_survey_submitted', JSON.stringify({
            ts: new Date().toISOString(),
            answers: formData,
            duration_ms: totalDurationMs,
          }));
        } catch {}
        try {
          localStorage.setItem('rt_flow_stage', 'done');
          localStorage.setItem('rt_last_route', '/thank-you');
        } catch {}
        navigate('/thank-you');
        
      } else {
        logEvent('post_survey_submit_failure', { status: resp.status });
        setError('Failed to submit survey. Please try again.');
      }
    } catch (err) {
      logEvent('post_survey_submit_error', { error: String(err) });
      setError('An error occurred. Please try again.');
    }
  };

  const progressPct = Math.round(((step + 1) / totalSteps) * 100);

  return (
    <div className="survey-container">
      <div className="survey-form-wrapper">
        <header className="survey-header">
          <h1>Post-Task Survey</h1>
          <p>Please answer the following questions about your experience.</p>

          <div className="progress-wrap" aria-label={`Progress: ${progressPct}%`}>
            <div className="progress-bar" style={{ width: `${progressPct}%` }} />
            <div className="progress-meta">
              <span>Section {step + 1} of {totalSteps}</span>
              <span>{progressPct}%</span>
            </div>
          </div>
        </header>

        {/* Block implicit submits so navigation always uses the buttons */}
        <form
          className="survey-form"
          onSubmit={(e) => e.preventDefault()}
          noValidate
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}
        >
          <section className="survey-section" aria-live="polite" key={current.key}>
            <h2>{current.title}</h2>

            {current.questions.map(q => (
              <LikertQuestion
                key={q.name}
                question={q.text}
                name={q.name}
                scale={current.scale}
                formData={formData}
                onChange={handleChange}
              />
            ))}
          </section>

          {error && <p className="survey-error" role="alert">{error}</p>}

          <div className="survey-nav" style={{ position: 'relative', zIndex: 10 }}>
            <button
              type="button"
              className="survey-back-btn"
              onClick={handleBack}
              disabled={step === 0}
            >
              Back
            </button>

            {step < totalSteps - 1 ? (
              <button
                type="button"
                className="survey-next-btn"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleNext(); }}
              >
                Next
              </button>
            ) : (
              <button
                type="button"
                className="survey-submit-btn"
                onClick={handleSubmit}
              >
                Submit
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

export default PostSurveyPage;
